import { useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Plus,
  X,
} from 'lucide-react';
import { useStripeContext } from '@/hooks/useStripeContext';
import PaymentMethodForm from './PaymentMethodForm';
import {
  type PaymentMethod,
  useAddPaymentMethod,
  usePaymentMethods,
  useRemovePaymentMethod,
  useRetryPayment,
  useSetDefaultPaymentMethod,
  useSubscription,
} from '@/hooks/useBilling';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { AppSheet } from '@/components/ui/app-sheet';
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
} from '@/components/ui/responsive-table';

function formatBrand(brand: string): string {
  if (!brand) return 'Card';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}

function formatExpiry(month: number, year: number): string {
  const mm = String(month).padStart(2, '0');
  const yy = String(year).slice(-2);
  return `${mm}/${yy}`;
}

export default function PaymentMethodsManagement() {
  const { stripePromise } = useStripeContext();
  const { data: subscription } = useSubscription();
  // Only a Stripe customer has payment methods to list; the backend has nothing to ask
  // Stripe for without one. Not the plan: an org that cancelled back to Free keeps its cards.
  const hasStripeCustomer = !!subscription?.stripe_customer_id;
  const { data: paymentMethods, isLoading, error } = usePaymentMethods(hasStripeCustomer);
  const addMutation = useAddPaymentMethod();
  const setDefaultMutation = useSetDefaultPaymentMethod();
  const removeMutation = useRemovePaymentMethod();
  const retryMutation = useRetryPayment();

  const [showAddModal, setShowAddModal] = useState(false);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const status = subscription?.subscription_status?.toLowerCase() ?? '';
  const needsRetry = status === 'past_due' || status === 'incomplete';

  if (!hasStripeCustomer) {
    return null;
  }

  const clearActionError = () => { setActionError(null); };

  const handleAddSuccess = async (paymentMethodId: string) => {
    setActionError(null);
    try {
      const setAsDefault = !paymentMethods || paymentMethods.length === 0;
      await addMutation.mutateAsync({
        payment_method_id: paymentMethodId,
        set_as_default: setAsDefault,
      });
      setShowAddModal(false);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to add payment method');
    }
  };

  const handleSetDefault = async (id: string) => {
    setActionError(null);
    try {
      await setDefaultMutation.mutateAsync(id);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to set default');
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemoveId) return;
    setActionError(null);
    try {
      await removeMutation.mutateAsync(pendingRemoveId);
      setPendingRemoveId(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to remove payment method');
    }
  };

  const handleRetryPayment = async () => {
    setActionError(null);
    try {
      await retryMutation.mutateAsync();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Retry failed');
    }
  };

  const pendingRemoveMethod = paymentMethods?.find((pm) => pm.id === pendingRemoveId) ?? null;

  const columns: readonly ResponsiveTableColumn<PaymentMethod>[] = [
    {
      key: 'card',
      label: 'Card',
      hideMobileLabel: true,
      render: (pm) => (
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium">{formatBrand(pm.brand)}</span>
          <span className="font-mono text-xs text-muted-foreground">•••• {pm.last4}</span>
          {pm.is_default ? (
            <Badge className="bg-sev-4/15 text-sev-4 border-0">Default</Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'expires',
      label: 'Expires',
      render: (pm) => (
        <span className="font-mono text-xs">{formatExpiry(pm.exp_month, pm.exp_year)}</span>
      ),
    },
  ];

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold">Payment methods</h2>
        <Button onClick={() => { clearActionError(); setShowAddModal(true); }}>
          <Plus className="w-4 h-4" />
          Add payment method
        </Button>
      </div>

      {actionError ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{actionError}</AlertDescription>
          <button
            type="button"
            onClick={clearActionError}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {retryMutation.isSuccess ? (
        <Alert
          className={`mb-4 relative ${retryMutation.data.success ? 'border-sev-4 bg-sev-4/10' : 'border-sev-3 bg-sev-3-soft'}`}
        >
          {retryMutation.data.success ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          <AlertDescription>{retryMutation.data.message}</AlertDescription>
          <button
            type="button"
            onClick={() => { retryMutation.reset(); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      {needsRetry ? (
        <Alert className="mb-4 border-sev-3 bg-sev-3-soft">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <p className="mb-2">
              Your subscription has an unpaid invoice. After confirming your default payment
              method below, click <strong>Retry payment</strong> to charge it again.
            </p>
            <Button
              onClick={() => { void handleRetryPayment(); }}
              disabled={retryMutation.isPending || !paymentMethods?.some((pm) => pm.is_default)}
              className="bg-sev-3 hover:bg-sev-3/90 text-foreground"
              size="sm"
            >
              {retryMutation.isPending ? 'Retrying…' : 'Retry payment'}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <strong>Failed to load payment methods:</strong> {error.message}
          </AlertDescription>
        </Alert>
      ) : null}

      {isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : paymentMethods && paymentMethods.length > 0 ? (
        <ResponsiveTable<PaymentMethod>
          columns={columns}
          rows={paymentMethods}
          rowKey={(r) => r.id}
          rowActions={(pm) => (
            <div className="flex items-center gap-2 justify-end">
              {!pm.is_default ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { void handleSetDefault(pm.id); }}
                  disabled={setDefaultMutation.isPending || removeMutation.isPending}
                >
                  Set default
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={() => { clearActionError(); setPendingRemoveId(pm.id); }}
                disabled={setDefaultMutation.isPending || removeMutation.isPending}
                className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
              >
                Remove
              </Button>
            </div>
          )}
        />
      ) : (
        <EmptyState
          variant="no-data"
          title="No payment methods on file"
          description="Add a payment method to manage your subscription."
          size="inline"
        />
      )}

      <AppSheet
        open={showAddModal}
        onOpenChange={setShowAddModal}
        title="Add payment method"
      >
        <AppSheet.Body>
          {showAddModal ? (
            <Elements stripe={stripePromise}>
              <PaymentMethodForm
                onSuccess={(pmId) => handleAddSuccess(pmId)}
                onCancel={() => { setShowAddModal(false); }}
                submitLabel="Save card"
                isLoading={addMutation.isPending}
              />
            </Elements>
          ) : null}
        </AppSheet.Body>
      </AppSheet>

      <AppSheet
        open={pendingRemoveMethod !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemoveId(null);
        }}
        title="Remove payment method"
      >
        <AppSheet.Body>
          <p className="text-sm">
            Remove{' '}
            <strong>
              {pendingRemoveMethod ? formatBrand(pendingRemoveMethod.brand) : ''} ••••{' '}
              {pendingRemoveMethod?.last4}
            </strong>
            ?
          </p>
          {pendingRemoveMethod?.is_default ? (
            <Alert className="mt-4 border-sev-3 bg-sev-3-soft">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                This is your default payment method. Removing it will leave your subscription
                with no way to charge future invoices until you add a new card.
              </AlertDescription>
            </Alert>
          ) : null}
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setPendingRemoveId(null); }}
            disabled={removeMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => { void handleConfirmRemove(); }}
            disabled={removeMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {removeMutation.isPending ? 'Removing…' : 'Remove'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>
    </section>
  );
}
